import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { startAuthentication } from "@simplewebauthn/browser";
import { api, setAccessToken, getAccessToken, onUnauthorized, API_URL } from "./api";
import {
  ensureIdentity,
  generateRegistrationIdentity,
  persistIdentity,
  clearIdentity,
  hasLocalIdentity,
  checkServerRecoveryBackup,
  restoreIdentityFromRecovery,
  ensureAutomaticBackupExists,
  invalidateIdentityCache,
  type RecoveryBackupInfo
} from "./e2ee";
import { snapshotActiveIdentity, restoreAccountIdentity, forgetAccountIdentity } from "./local-store";
import { WrongRecoveryPassphraseError } from "./crypto";
import type { User } from "@/types";

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  /** Non-null when this device has no local identity AND the account has a
   * server-side recovery backup - the app should prompt for the passphrase
   * before showing any chat content, rather than silently generating a
   * throwaway identity that can't decrypt anything old. */
  pendingRecovery: RecoveryBackupInfo | null;
  completeRecovery: (secret: string) => Promise<void>;
  /** User doesn't have/remember the secret - falls through to the old
   * behavior (generate a fresh throwaway identity). */
  skipRecovery: () => Promise<void>;
  login: (email: string, password: string, turnstileToken?: string) => Promise<{ requiresTotp: true; pendingToken: string } | void>;
  completeTotpLogin: (pendingToken: string, code: string, password?: string) => Promise<void>;
  /** For the NEW device in a QR-code login (see migration 0043) - the identity
   * here is already in hand via the sealed payload, never something to
   * recover from a password, so this deliberately never touches
   * establishIdentity()'s recovery-backup logic. */
  completeQrLogin: (user: User, accessToken: string, identity: { publicKey: string; privateKey: string }) => Promise<void>;
  /** Alternative to login() above - proves identity via a registered passkey
   * (WebAuthn) instead of a password, then joins the exact same
   * establishIdentity() flow every other login path funnels through. There's
   * no password to hand establishIdentity here (a passkey never produces
   * one), so this always takes the same no-password fallback branch the
   * silent cookie-restore-on-page-load path already uses - see
   * establishIdentity's comment. */
  loginWithPasskey: () => Promise<void>;
  /** Step 1 of signup - generates this device's E2EE identity and starts the
   * OTP-gated flow. No account exists and nothing is logged in yet; see
   * verifyRegisterOtp for the step that actually creates and signs into the
   * account. Throws if the email/username is already taken. */
  startRegister: (email: string, username: string, password: string, displayName: string, turnstileToken?: string) => Promise<void>;
  /** Step 2 - the account is only actually created once this succeeds. On
   * success, logs in exactly like every other login path. */
  verifyRegisterOtp: (code: string) => Promise<void>;
  /** Re-sends a fresh code for the signup started by the most recent
   * startRegister call. */
  resendRegisterOtp: () => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  // ---------- Multi-account switching ----------
  // Accounts added to this device this session, other than the one currently
  // active. Deliberately kept in memory only, never persisted to
  // localStorage/IndexedDB - access tokens are short-lived and sensitive,
  // matching how the primary session's own token is already handled. This
  // means: switching between added accounts works seamlessly within this tab
  // for as long as their tokens stay valid, but a page reload only restores
  // whichever account was active - others need to be added again. That's an
  // honest limitation of this app's single refresh-token-cookie auth model,
  // not something worth hiding from the person using it.
  otherAccounts: { userId: string; user: User }[];
  /** Logs into a second (or third...) account without logging out of the
   * current one. The outgoing account's identity is snapshotted first so it
   * can be switched back to without re-entering its password. */
  addAccount: (
    email: string,
    password: string
  ) => Promise<{ requiresTotp: true; pendingToken: string } | { needsRecovery: RecoveryBackupInfo } | void>;
  completeAddAccountTotp: (
    pendingToken: string,
    code: string,
    password?: string
  ) => Promise<{ needsRecovery: RecoveryBackupInfo } | void>;
  completeAddAccountRecovery: (backup: RecoveryBackupInfo, secret: string) => Promise<void>;
  /** Returns false (and touches nothing about the current session) if the
   * target account's stored token has expired - the caller should prompt to
   * re-add that account rather than assume the switch happened. */
  switchToAccount: (userId: string) => Promise<boolean>;
  /** Forgets this account on this device - removes it from the switcher and
   * discards its saved identity snapshot. Does not affect its session on any
   * other device, and does not touch the currently active account. */
  removeOtherAccount: (userId: string) => void;
  /** True when this session has no local identity, a backup exists but
   * wasn't used (or none exists but other sessions for this account are
   * active), and the person needs to explicitly choose sync vs. start fresh
   * - see establishIdentity's own comment for why this exists. */
  pendingIdentityChoice: boolean;
  /** Explicit, user-confirmed "start fresh" - the only caller anywhere that
   * passes confirmReplace: true to ensureIdentity(). */
  confirmStartFresh: () => Promise<void>;
  /** Dismisses the identity-choice prompt without generating anything -
   * used once a sync completes successfully while the prompt was open. */
  dismissIdentityChoice: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [pendingRecovery, setPendingRecovery] = useState<RecoveryBackupInfo | null>(null);
  const [pendingIdentityChoice, setPendingIdentityChoice] = useState(false);
  // Held only in memory for the lifetime of this component - a page refresh
  // between starting signup and entering the code loses this on purpose (see
  // startRegister's own comment for why that's safe: the OTP-entry step
  // becomes unreachable, and re-submitting the form generates a fresh
  // identity and correctly replaces the stale pending signup server-side).
  const [pendingSignup, setPendingSignup] = useState<{ email: string; password: string; identity: { publicKey: string; privateKey: string } } | null>(
    null
  );
  // Single source of truth for accounts other than the currently active one -
  // includes the raw token internally (needed to actually switch to them),
  // but the public otherAccounts value exposed below strips it out, since
  // consumers of useAuth() only need to know which accounts exist to render a
  // switcher, never the raw token itself.
  const [otherAccountSessions, setOtherAccountSessions] = useState<{ userId: string; user: User; accessToken: string }[]>([]);

  useEffect(() => {
    onUnauthorized(() => {
      setAccessToken(null);
      setUser(null);
    });
  }, []);

  // The single choke point every login path funnels through before the app is
  // allowed to show any chat content.
  //
  // Default behavior: every account's identity is automatically backed up using
  // its own login password as the secret (see ensureAutomaticBackupExists) - so
  // logging in on a new device with the correct password silently restores full
  // history, no extra step, multiple devices just stay in sync. Only when someone
  // has explicitly set a separate, stronger passphrase does the login password
  // stop being enough - at that point this falls through to an explicit prompt.
  //
  // loginPassword is only available right after someone actually types it in
  // (the login/register forms) - a cookie-based silent session restore on page
  // load never has it, so that path always falls through to an explicit prompt
  // if a backup exists at all, since there's nothing to try silently.
  /**
   * Returns the recovery backup info if this identity needs a passphrase to
   * unlock, or null if it was established successfully (generated, restored
   * automatically, or already local). Deliberately does NOT set
   * pendingRecovery itself - the normal login/register paths do that with the
   * return value, but addAccount below needs to handle a recovery requirement
   * without hijacking the global gate that ProtectedRoute uses to block the
   * WHOLE app, which would incorrectly block access to the already-active
   * account just because a second one being added needs its own passphrase.
   */
  async function establishIdentity(loginPassword?: string): Promise<RecoveryBackupInfo | null> {
    if (await hasLocalIdentity()) {
      await ensureIdentity();
      // Opportunistic, non-destructive auto-enroll: covers accounts that predate
      // this feature, or whose very first backup attempt failed. Never touches an
      // existing backup (custom or automatic) - see ensureAutomaticBackupExists.
      if (loginPassword) ensureAutomaticBackupExists(loginPassword).catch(() => {});
      return null;
    }

    const backup = await checkServerRecoveryBackup();
    if (!backup) {
      // No backup on file yet - but that doesn't guarantee this is genuinely
      // a first-ever device: a session that only ever used the silent
      // cookie-restore path (never an explicit password login) would never
      // have triggered ensureAutomaticBackupExists, even with a real,
      // working identity already active elsewhere. Check for other active
      // sessions before assuming it's safe to generate and publish a new
      // identity - this is exactly the check whose absence let one browser
      // tab silently overwrite another session's real key.
      const hasOtherSessions = await checkOtherActiveSessions();
      if (hasOtherSessions) {
        setPendingIdentityChoice(true);
        return null;
      }
      await ensureIdentity();
      if (loginPassword) ensureAutomaticBackupExists(loginPassword).catch(() => {});
      return null;
    }

    if (loginPassword) {
      try {
        await restoreIdentityFromRecovery(loginPassword, backup);
        return null; // seamless - this is the default path most people will hit
      } catch (err) {
        if (!(err instanceof WrongRecoveryPassphraseError)) throw err;
        // The login password itself didn't open it - this account has a separate,
        // stronger passphrase set. Fall through to asking for it explicitly.
      }
    }

    return backup;
  }

  async function completeRecovery(secret: string) {
    if (!pendingRecovery) return;
    await restoreIdentityFromRecovery(secret, pendingRecovery);
    setPendingRecovery(null);
  }

  async function skipRecovery() {
    setPendingRecovery(null);
    // Deliberately does NOT call ensureIdentity() here anymore - a backup
    // WAS found (a real identity exists somewhere for this account), so
    // silently generating a replacement here is exactly the destructive
    // overwrite this whole gate exists to prevent. Surface the same explicit
    // choice used everywhere else instead.
    setPendingIdentityChoice(true);
  }

  /** Best-effort: if this check itself fails, default to false (proceed as
   * before generating a fresh identity) rather than lock someone out of
   * their own app over a transient network error - the choice prompt is a
   * safety improvement, not a hard gate that must always succeed. */
  async function checkOtherActiveSessions(): Promise<boolean> {
    try {
      const sessions = await api.get<{ is_current: boolean }[]>("/api/auth/sessions");
      return sessions.some((s) => !s.is_current);
    } catch {
      return false;
    }
  }

  async function confirmStartFresh() {
    await ensureIdentity({ confirmReplace: true });
    setPendingIdentityChoice(false);
  }

  function dismissIdentityChoice() {
    setPendingIdentityChoice(false);
  }

  // On first load there's no access token in memory (page refresh clears it),
  // but the httpOnly refresh cookie may still be valid - try to silently restore.
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(
          `${import.meta.env.VITE_API_URL ?? "http://localhost:4000"}/api/auth/refresh`,
          { method: "POST", credentials: "include" }
        );
        if (res.ok) {
          const data = await res.json();
          setAccessToken(data.accessToken);
          const me = await api.get<User>("/api/auth/me");
          setUser(me);
          const recovery = await establishIdentity();
          if (recovery) setPendingRecovery(recovery);
        }
      } catch {
        // no valid session, that's fine
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  async function login(email: string, password: string, turnstileToken?: string) {
    const data = await api.post<
      { user: User; accessToken: string } | { requiresTotp: true; pendingToken: string }
    >("/api/auth/login", { email, password, turnstileToken });

    if ("requiresTotp" in data) return data;
    if ("requiresLoginChallenge" in data) return data;

    setAccessToken(data.accessToken);
    setUser(data.user);
    const recovery = await establishIdentity(password);
    if (recovery) setPendingRecovery(recovery);
  }

  async function completeTotpLogin(pendingToken: string, code: string, password?: string) {
    const data = await api.post<{ user: User; accessToken: string }>("/api/auth/login/verify-2fa", {
      pendingToken,
      code
    });
    setAccessToken(data.accessToken);
    setUser(data.user);
    const recovery = await establishIdentity(password);
    if (recovery) setPendingRecovery(recovery);
  }

  async function completeQrLogin(user: User, accessToken: string, identity: { publicKey: string; privateKey: string }) {
    // Deliberately NOT establishIdentity() - the identity here came straight
    // out of the sealed QR payload, already fully formed, so there is nothing
    // to check a server recovery backup for and nothing to recover.
    await persistIdentity(identity);
    setAccessToken(accessToken);
    setUser(user);
  }

  async function loginWithPasskey() {
    const { options, challengeId } = await api.post<{ options: unknown; challengeId: string }>(
      "/api/auth/webauthn/login/options"
    );
    // VERIFIED against the actually-installed @simplewebauthn/browser@10.0.0
    // type definitions: startAuthentication takes optionsJSON as a direct
    // positional argument in this version, NOT wrapped in an object - the
    // v9->v10 changelog summary this was originally written from suggested
    // otherwise, but the installed .d.ts confirms the flat signature.
    const response = await startAuthentication(options as Parameters<typeof startAuthentication>[0]);
    const data = await api.post<{ user: User; accessToken: string }>("/api/auth/webauthn/login/verify", {
      challengeId,
      response
    });
    setAccessToken(data.accessToken);
    setUser(data.user);
    const recovery = await establishIdentity();
    if (recovery) setPendingRecovery(recovery);
  }

  async function startRegister(email: string, username: string, password: string, displayName: string, turnstileToken?: string) {
    // Same as before: generated before the account even exists - the public
    // key rides along with the /start call, the private key never leaves
    // this device. Held in pendingSignup (not persisted) until verification
    // actually succeeds - see verifyRegisterOtp.
    const identity = generateRegistrationIdentity();
    await api.post("/api/auth/register/start", { email, username, password, displayName, publicKey: identity.publicKey, turnstileToken });
    setPendingSignup({ email, password, identity });
  }

  async function verifyRegisterOtp(code: string) {
    if (!pendingSignup) throw new Error("No signup in progress");
    const { email, password, identity } = pendingSignup;
    const data = await api.post<{ user: User; accessToken: string }>("/api/auth/register/verify", { email, code });
    await persistIdentity(identity);
    setAccessToken(data.accessToken);
    setUser(data.user);
    setPendingSignup(null);
    // Best-effort - every account starts with the automatic password-derived
    // backup in place from the very first moment, so any future device just
    // logging in with the right password picks up full history with no extra
    // step. A failure here shouldn't block registration itself; establishIdentity
    // will opportunistically retry this on the next successful login anyway.
    ensureAutomaticBackupExists(password).catch(() => {});
  }

  async function resendRegisterOtp() {
    if (!pendingSignup) throw new Error("No signup in progress");
    await api.post("/api/auth/register/resend", { email: pendingSignup.email });
  }

  // ---------- Multi-account switching ----------
  async function addAccount(email: string, password: string) {
    // Snapshot the OUTGOING account before establishIdentity below changes
    // which identity is active - there'd be nothing left to snapshot if this
    // ran after.
    if (user) {
      await snapshotActiveIdentity(user.id);
      setOtherAccountSessions((prev) => [
        ...prev.filter((a) => a.userId !== user.id),
        { userId: user.id, user, accessToken: getAccessToken() ?? "" }
      ]);
    }
    // Critical: without this, establishIdentity() below checks the in-memory
    // identity cache BEFORE ever looking at storage - it would still hold the
    // OUTGOING account's keys at this point, and would wrongly conclude "I
    // already have a local identity" and silently keep using them for the new
    // account.
    invalidateIdentityCache();

    // Deliberately NOT reusing login() here - that function calls
    // establishIdentity() and sets the GLOBAL pendingRecovery state on the
    // result, which ProtectedRoute uses to block the ENTIRE app. If the
    // account being added has its own separate recovery passphrase, that
    // would incorrectly lock the person out of the account they're already
    // using, just because a second one needs its own passphrase. This inline
    // version handles that result locally instead, returning it to the
    // add-account dialog so it can prompt for the passphrase in place.
    const data = await api.post<
      { user: User; accessToken: string } | { requiresTotp: true; pendingToken: string }
    >("/api/auth/login", { email, password, turnstileToken });

    if ("requiresTotp" in data) return data;

    setAccessToken(data.accessToken);
    setUser(data.user);
    const recovery = await establishIdentity(password);
    if (recovery) return { needsRecovery: recovery };
  }

  /** Completes the recovery flow addAccount above returned - deliberately
   * separate from completeRecovery, which reads from the global
   * pendingRecovery state this path never touches. */
  async function completeAddAccountRecovery(backup: RecoveryBackupInfo, secret: string) {
    await restoreIdentityFromRecovery(secret, backup);
  }

  /** The add-account-aware counterpart to completeTotpLogin - same API call,
   * but handles a recovery requirement locally instead of setting the global
   * pendingRecovery state, for exactly the same reason addAccount's own login
   * call does above. Needed because an account can have BOTH 2FA and a custom
   * recovery passphrase; fixing only the initial login call would leave this
   * combination still hijacking the global gate at the 2FA step. */
  async function completeAddAccountTotp(pendingToken: string, code: string, password?: string) {
    const data = await api.post<{ user: User; accessToken: string }>("/api/auth/login/verify-2fa", {
      pendingToken,
      code
    });
    setAccessToken(data.accessToken);
    setUser(data.user);
    const recovery = await establishIdentity(password);
    if (recovery) return { needsRecovery: recovery };
  }

  /**
   * Validates the target's stored token with a raw, unauthenticated-style
   * fetch - deliberately NOT going through api.ts's normal request(), whose
   * 401 handling calls the shared refresh endpoint using whatever refresh
   * cookie is CURRENTLY set. Since that cookie belongs to the currently
   * active account, not the one being switched to, letting that machinery run
   * here could silently mint a fresh token for the WRONG account and report
   * success while the wrong identity ends up active - a correctness bug, not
   * just a failed switch. This check only ever reads; it never triggers a
   * refresh for anyone.
   */
  async function switchToAccount(targetUserId: string): Promise<boolean> {
    const target = otherAccountSessions.find((a) => a.userId === targetUserId);
    if (!target) return false;

    const res = await fetch(`${API_URL}/api/auth/me`, {
      headers: { Authorization: `Bearer ${target.accessToken}` }
    }).catch(() => null);
    if (!res || !res.ok) return false;
    const freshUser: User = await res.json();

    if (user) {
      await snapshotActiveIdentity(user.id);
    }
    const restored = await restoreAccountIdentity(targetUserId);
    if (!restored) return false; // shouldn't happen if addAccount snapshotted correctly, but don't half-switch if it did
    invalidateIdentityCache();

    setOtherAccountSessions((prev) => [
      ...prev.filter((a) => a.userId !== targetUserId),
      ...(user ? [{ userId: user.id, user, accessToken: getAccessToken() ?? "" }] : [])
    ]);
    setAccessToken(target.accessToken);
    setUser(freshUser);
    return true;
  }

  function removeOtherAccount(targetUserId: string) {
    setOtherAccountSessions((prev) => prev.filter((a) => a.userId !== targetUserId));
    forgetAccountIdentity(targetUserId).catch(() => {});
  }

  async function logout() {
    await api.post("/api/auth/logout").catch(() => {});
    setAccessToken(null);
    setUser(null);
    setPendingRecovery(null);
    setOtherAccountSessions([]);
    // Wipe the private key and decrypted message cache from this device - a
    // different account logging in on the same browser starts with a clean slate.
    await clearIdentity();
  }

  async function refreshUser() {
    const me = await api.get<User>("/api/auth/me");
    setUser(me);
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        pendingRecovery,
        completeRecovery,
        skipRecovery,
        login,
        completeTotpLogin,
        completeQrLogin,
        loginWithPasskey,
        startRegister,
        verifyRegisterOtp,
        resendRegisterOtp,
        logout,
        refreshUser,
        otherAccounts: otherAccountSessions.map(({ userId, user: u }) => ({ userId, user: u })),
        addAccount,
        completeAddAccountTotp,
        completeAddAccountRecovery,
        switchToAccount,
        removeOtherAccount,
        pendingIdentityChoice,
        confirmStartFresh,
        dismissIdentityChoice
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
